import { Box, Paper, Typography } from "@mui/material";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { getChartSliceColor } from "../../../utils/distinctColors";

export type PipelineSummarySegment = {
  name: string;
  value: number;
  pct: number;
  dealCount?: number;
};

type PipelineSummaryDonutChartProps = {
  title: string;
  items: PipelineSummarySegment[];
  totalValue: number;
  totalDeals: number;
  formatValue: (value: number) => string;
  minHeight: number;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: PipelineSummarySegment & { fill?: string };
  }>;
  formatValue: (value: number) => string;
};

function ChartTooltip({ active, payload, formatValue }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        zIndex: 20,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#182737" }}>
        {item.name}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
        {formatValue(item.value)} · {Math.round(item.pct)}%
        {item.dealCount != null ? ` · ${item.dealCount} deals` : ""}
      </Typography>
    </Paper>
  );
}

export function PipelineSummaryDonutChart({
  title,
  items,
  totalValue,
  totalDeals,
  formatValue,
  minHeight,
}: PipelineSummaryDonutChartProps) {
  if (items.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          minHeight,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 2.5,
          py: 2,
        }}
      >
        <Typography className="text-general-section-title" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#182737" }}>
          No data for the current filters.
        </Typography>
      </Paper>
    );
  }

  const chartData = items.map((item, index) => ({
    ...item,
    fill: getChartSliceColor(index),
  }));

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        background:
          "linear-gradient(180deg, #FAFBFC 0%, #FFFFFF 28%, #FFFFFF 100%)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography className="text-general-section-title">
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 700, color: "#182737" }}>
            {totalDeals}
          </Box>{" "}
          deals
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" },
          gap: { xs: 1.5, lg: 1.5 },
          px: { xs: 1.5, md: 2 },
          py: 2,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: { xs: 220, lg: 280 },
            height: "100%",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              px: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 700,
                color: "#182737",
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              {formatValue(totalValue)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
              Total Volume
            </Typography>
          </Box>

          <Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="52%"
                  outerRadius="92%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={0}
                  stroke="none"
                  strokeWidth={0}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} stroke="none" />
                  ))}
                </Pie>
                <Tooltip
                  content={<ChartTooltip formatValue={formatValue} />}
                  wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            minHeight: 0,
            overflowY: "auto",
            pr: 0.5,
            justifyContent: "center",
          }}
        >
          {chartData.map((item) => (
            <Box
              key={item.name}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: 0.75,
                px: 0.75,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: "rgba(15, 23, 42, 0.02)",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.fill,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  title={item.name}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#182737",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mb: 0.25,
                    lineHeight: 1.2,
                  }}
                >
                  {item.name}
                </Typography>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 999,
                    backgroundColor: "#E8EDF2",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${item.pct}%`,
                      height: "100%",
                      borderRadius: 999,
                      backgroundColor: item.fill,
                      minWidth: item.value > 0 ? 3 : 0,
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ textAlign: "right", flexShrink: 0, pl: 0.25 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#182737",
                    lineHeight: 1.1,
                  }}
                >
                  {Math.round(item.pct)}%
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
